import {
  CheckboxFormikField,
  Container,
  Divider,
  Grid,
  GridItem,
  Loader,
  SectionMessage,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { FieldArray, Form, Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { DocumentQuestion } from "./DocumentQuestion";
import { TemplateLayout } from "./TemplateLayout";
import { useData } from "./hooks/useData";

import { PageTitle } from "../../../components/page-title/PageTitle";

export function Template() {
  const { id } = useParams();
  const {
    isLoadingQuestions,
    questionsError,
    initialValues,
    questions,
    schema,
    submit,
    getSelectedQuestions,
  } = useData(id);
  const { t } = useTranslation();
  const isDetailsMode = !!id;

  if (isLoadingQuestions) {
    return <Loader />;
  }

  if (questionsError) {
    return (
      <SectionMessage type="danger">
        {t("Something went wrong fetching the list of questions")}
      </SectionMessage>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        await submit(
          {
            ...values,
            is_disclaimer_accepted: true,
            input_claims_with_prompts: getSelectedQuestions(
              values.questions,
              values.documents
            ),
          },
          helpers
        );
      }}
    >
      {({ isValid, errors, values, validateForm, handleSubmit }) => (
        <Form>
          <TemplateLayout
            templateType="TENANCY"
            isValid={isValid}
            handleSubmit={handleSubmit}
            validateForm={validateForm}
          >
            <Container layout="applicant/narrow">
              <Stack gap="space-3">
                <PageTitle>
                  {isDetailsMode ? (
                    initialValues.name
                  ) : (
                    <Trans>Create new questionnaire template</Trans>
                  )}
                </PageTitle>
              </Stack>
              <Container>
                <Grid>
                  <GridItem>
                    <TextInputFormikField
                      label={t("Template name")}
                      testID="template-name"
                      name="name"
                      isRequired
                    />
                  </GridItem>
                  <GridItem className={styles.gapOne}>
                    <Text style="form/section-heading">
                      <Trans>Questions</Trans>
                    </Text>
                    <Text style="form/body-secondary">
                      <Trans>
                        Select the questions you want to appear in your
                        questionnaire.
                      </Trans>
                    </Text>
                  </GridItem>
                  {errors && errors.questions && (
                    <GridItem>
                      <SectionMessage type="danger">
                        <Trans>Please specify at least one question.</Trans>
                      </SectionMessage>
                    </GridItem>
                  )}
                  <FieldArray
                    name="questions"
                    render={() => (
                      <>
                        {values.questions &&
                          values.questions.length > 0 &&
                          values.questions.map((question, index) => (
                            <GridItem key={index}>
                              <CheckboxFormikField
                                name={`questions.${index}.selected`}
                                testID={`questions.${index}.selected`}
                                description={question.prompt}
                                aria-label={question.claim_name}
                                isCard
                                noBorder
                              >
                                {question.question}
                              </CheckboxFormikField>
                            </GridItem>
                          ))}
                      </>
                    )}
                  />
                </Grid>
                <Divider />
                <Grid>
                  <GridItem className={css({ gap: "var(--space-1)" })}>
                    <Text style="form/section-heading">
                      <Trans>Document upload types</Trans>
                    </Text>
                    <Text style="form/body-secondary">
                      <Trans>
                        Select any document types you want to collect. Please
                        note that any instructions which are edited will not be
                        automatically translated. Consider creating a duplicate
                        template for alternate languages.
                      </Trans>
                    </Text>
                  </GridItem>
                  {errors && errors.questions && (
                    <GridItem>
                      <SectionMessage type="danger">
                        <Trans>
                          Please specify at least one document upload type.
                        </Trans>
                      </SectionMessage>
                    </GridItem>
                  )}
                  <FieldArray
                    name="documents"
                    render={() => (
                      <>
                        {values.documents &&
                          values.documents.length > 0 &&
                          values.documents.map((document, index) => (
                            <DocumentQuestion
                              question={questions?.find(
                                (q) => q.claim_name === document.claim_name
                              )}
                              key={index}
                              field={`documents.${index}`}
                              document={document}
                            />
                          ))}
                      </>
                    )}
                  />
                </Grid>
              </Container>
            </Container>
          </TemplateLayout>
        </Form>
      )}
    </Formik>
  );
}

const styles = {
  gapOne: css({ gap: "var(--space-1)" }),
};
